import { useState, useRef } from 'react'
import { api, getErrorMessage } from '@/shared/api/generated'
import type { RecordRow, Tag, ViewResponse } from '@/shared/api/generated'
import { useAuthStore } from '@/features/auth/stores/authStore'
import { useFileBrowserStore } from '@/features/files/stores/fileBrowserStore'
import { toast } from '@/shared/stores/toastStore'

interface RecordTableProps {
  records: RecordRow[]
  tags: Tag[]
  labelFormat: string
  sortColumn: string
  sortDirection: 'asc' | 'desc'
  onSort: (column: string) => void
  onClickRecord: (record: RecordRow) => void
  onEditRecord: (record: RecordRow) => void
  views: ViewResponse[]
  groupId: number
  onViewsChanged: () => void
  tagFilters: Record<number, string>
  onTagFilter: (tagId: number, text: string) => void
  columnFilters: Record<string, string>
  onColumnFilter: (column: string, text: string) => void
  getAutocomplete: (tagId: number) => string[]
}

function formatLabel(id: number, format: string): string {
  if (!format) return String(id).padStart(5, '0')
  // Simple %0Nd support
  const match = format.match(/%0?(\d+)d/)
  if (match) return String(id).padStart(Number(match[1]), '0')
  return String(id)
}

export function RecordTable({ records, tags, labelFormat, sortColumn, sortDirection, onSort, onClickRecord, onEditRecord, views, groupId, onViewsChanged, tagFilters, onTagFilter, columnFilters, onColumnFilter, getAutocomplete }: RecordTableProps) {
  const userId = useAuthStore(s => s.user?.id)
  const [busy, setBusy] = useState<number | null>(null)

  function viewForRecord(rec: RecordRow): ViewResponse | undefined {
    return views.find(v => v.base_path === rec.path && v.created_by === userId)
  }

  async function toggleView(rec: RecordRow, e: React.MouseEvent) {
    e.stopPropagation()
    if (busy) return
    setBusy(rec.id)
    try {
      const existing = viewForRecord(rec)
      if (existing) {
        await api.deleteView(existing.id)
      } else {
        await api.createView(groupId, {
          name: formatLabel(rec.id, labelFormat),
          base_path: rec.path,
          description: '',
          personal: true,
        })
      }
      onViewsChanged()
      useFileBrowserStore.getState().refresh()
    } catch (err) {
      toast.error(getErrorMessage(err))
    } finally {
      setBusy(null)
    }
  }
  const sortIndicator = (col: string) => {
    if (sortColumn !== col) return ''
    return sortDirection === 'asc' ? ' ▲' : ' ▼'
  }

  return (
    <div className="registry-table-wrap" data-testid="registry-table">
      <table className="registry-table">
        <thead>
          <tr>
            <th className="col-color"></th>
            <th className="col-id sortable" onClick={() => onSort('id')}>
              ID{sortIndicator('id')}
            </th>
            <th className="col-view" title="Mount for WebDAV/SSHFS">⏏</th>
            <th className="col-edit"></th>
            {tags.map(tag => (
              <th key={tag.id} className="sortable" onClick={() => onSort(String(tag.id))}>
                {tag.name}{sortIndicator(String(tag.id))}
              </th>
            ))}
            <th className="sortable" onClick={() => onSort('updated_by')}>
              Updated By{sortIndicator('updated_by')}
            </th>
            <th className="sortable" onClick={() => onSort('updated_at')}>
              Updated{sortIndicator('updated_at')}
            </th>
          </tr>
          {tags.length > 0 && (
            <tr className="filter-row">
              <th></th>
              <th>
                <input
                  type="text"
                  className="filter-cell-input"
                  placeholder="ID"
                  value={columnFilters['id'] || ''}
                  onChange={e => onColumnFilter('id', e.target.value)}
                  data-testid="col-filter-id"
                />
              </th>
              <th></th><th></th>
              {tags.map(tag => (
                <th key={tag.id}>
                  <FilterCell
                    tagId={tag.id}
                    value={tagFilters[tag.id] || ''}
                    placeholder={tag.name}
                    onChange={onTagFilter}
                    suggestions={getAutocomplete(tag.id)}
                  />
                </th>
              ))}
              <th>
                <input
                  type="text"
                  className="filter-cell-input"
                  placeholder="Updated By"
                  value={columnFilters['updated_by'] || ''}
                  onChange={e => onColumnFilter('updated_by', e.target.value)}
                  data-testid="col-filter-updated-by"
                />
              </th>
              <th>
                <input
                  type="text"
                  className="filter-cell-input"
                  placeholder="Updated"
                  value={columnFilters['updated_at'] || ''}
                  onChange={e => onColumnFilter('updated_at', e.target.value)}
                  data-testid="col-filter-updated-at"
                />
              </th>
            </tr>
          )}
        </thead>
        <tbody>
          {records.map(rec => (
            <tr
              key={rec.id}
              onClick={() => onClickRecord(rec)}
              onDoubleClick={() => onEditRecord(rec)}
              className="registry-row"
              data-testid="registry-row"
            >
              <td className="col-color">
                {rec.color && <span className={`color-dot color-${rec.color}`} />}
              </td>
              <td className="col-id">{formatLabel(rec.id, labelFormat)}</td>
              <td className="col-view">
                <button
                  className={`view-toggle${viewForRecord(rec) ? ' active' : ''}`}
                  onClick={(e) => toggleView(rec, e)}
                  disabled={busy === rec.id}
                  title={viewForRecord(rec) ? 'Remove mount' : 'Mount for WebDAV/SSHFS'}
                  data-testid="view-toggle"
                >
                  {viewForRecord(rec) ? '⏏' : '⎘'}
                </button>
              </td>
              <td className="col-edit">
                <button
                  className="edit-btn"
                  onClick={(e) => { e.stopPropagation(); onEditRecord(rec) }}
                  title="Edit record"
                  data-testid="record-edit-btn"
                >
                  ✎
                </button>
              </td>
              {tags.map(tag => (
                <td key={tag.id}>{(rec.tag_values?.[tag.id] || []).join(', ')}</td>
              ))}
              <td>{rec.updated_by}</td>
              <td className="col-date">{new Date(rec.updated_at).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function FilterCell({ tagId, value, placeholder, onChange, suggestions }: {
  tagId: number; value: string; placeholder: string
  onChange: (tagId: number, text: string) => void; suggestions: string[]
}) {
  const [show, setShow] = useState(false)
  const ref = useRef<HTMLInputElement>(null)
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 })
  const filtered = value.length >= 2
    ? suggestions.filter(s => s.toLowerCase().includes(value.toLowerCase()))
    : []

  const updatePos = () => {
    if (ref.current) {
      const r = ref.current.getBoundingClientRect()
      setPos({ top: r.bottom, left: r.left, width: r.width })
    }
  }

  return (
    <div className="registry-filter-input">
      <label className="sr-only">{placeholder}:</label>
      <div className="filter-autocomplete">
        <input
          ref={ref}
          type="text"
          className="filter-cell-input"
          value={value}
          placeholder={placeholder}
          onChange={e => { onChange(tagId, e.target.value); updatePos(); setShow(true) }}
          onFocus={() => { updatePos(); setShow(true) }}
          onBlur={() => setTimeout(() => setShow(false), 150)}
          data-testid={`tag-filter-${tagId}`}
        />
        {show && filtered.length > 0 && (
          <ul className="autocomplete-list" style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 99999 }}>
            {filtered.slice(0, 20).map(s => (
              <li key={s} onMouseDown={() => { onChange(tagId, s); setShow(false) }}>{s}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
